import React, { useEffect, useMemo, useState } from 'react';
import {
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCol,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CFormSwitch,
    CFormTextarea,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
    CTooltip,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilArrowBottom, cilArrowLeft, cilArrowTop, cilCheck, cilCloudUpload, cilImage, cilLayers, cilPlus, cilStar, cilTrash } from '@coreui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useStore } from '../../contexts/StoreContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Product, ProductVariant } from '../../types/product';
import { ComboFormData, ComboImage, ComboItem } from '../../types/combo';
import { formatCOP } from './ComboList';

const genId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// combos.name es varchar(45)
const NAME_MAX_LENGTH = 45;
const VARIANT_STATUS_ACTIVE = 1;

// Nombre de la variante según sus valores de opción (ej. "Universitario / Ardiente"); vacío en productos simples
const variantLabel = (product: Product, variant: ProductVariant) => {
    if (!product.has_variants) return '';
    const valueNames = new Map<number, string>();
    (product.options ?? []).forEach((opt) => opt.values.forEach((val) => valueNames.set(val.id, val.value)));
    const label = (variant.option_value_ids ?? []).map((id) => valueNames.get(id)).filter(Boolean).join(' / ');
    return label || variant.name;
};

const ComboForm: React.FC = () => {
    const navigate = useNavigate();
    const { storeId } = useStore();
    const { id: comboId } = useParams<{ id: string }>();
    const isEdit = !!comboId;
    const { showToast } = useNotification();

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<ComboFormData>({ name: '', description: '', price: 0, packaging_fee: 0, stock: '', is_active: true });
    const [items, setItems] = useState<ComboItem[]>([]);
    const [images, setImages] = useState<ComboImage[]>([]);

    // Catálogo de la tienda para elegir productos (sin combos)
    const [products, setProducts] = useState<Product[]>([]);
    const [pickProductId, setPickProductId] = useState('');
    const [pickVariantId, setPickVariantId] = useState('');
    const [pickQuantity, setPickQuantity] = useState(1);

    useEffect(() => {
        if (!storeId) return;
        DomicilappService.getProductsByStore(storeId)
            .then((response) => setProducts((response?.data?.items ?? []).filter((p) => !p.is_combo)))
            .catch(() => setProducts([]));
    }, [storeId]);

    // Edición: carga el combo
    useEffect(() => {
        if (!isEdit || !storeId || !comboId) return;
        setLoading(true);
        DomicilappService.getComboForm(storeId, comboId)
            .then((response) => {
                const data = response?.data;
                if (response?.status !== 200 || !data?.combo) {
                    showToast({ title: 'Combo no disponible', message: response?.message ?? 'No se encontró el combo en esta tienda', type: 'danger', delay: 3500 });
                    navigate('/combos/list');
                    return;
                }
                setFormData({ ...data.combo, description: data.combo.description ?? '', stock: data.combo.stock ?? '', packaging_fee: Number(data.combo.packaging_fee ?? 0) });
                setItems(data.items.map((item) => ({ ...item, id: String(item.id) })));
                setImages(data.images.map((img) => ({ id: String(img.id), preview_url: img.url, position: img.position, is_main: img.is_main })));
            })
            .catch(() => showToast({ title: '¡Ha ocurrido un error!', message: 'No fue posible cargar el combo', type: 'danger', delay: 3500 }))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, storeId, comboId]);

    // Libera los blob: URLs al salir
    useEffect(() => {
        return () => images.forEach((img) => img.file && URL.revokeObjectURL(img.preview_url));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ===================== PRODUCTOS DEL COMBO =====================
    const pickedProduct = products.find((p) => String(p.id) === pickProductId);
    const pickedVariants: ProductVariant[] = pickedProduct?.variants ?? [];

    const handlePickProduct = (productId: string) => {
        setPickProductId(productId);
        const product = products.find((p) => String(p.id) === productId);
        // Producto simple (una sola variante): se elige sola
        const variants = product?.variants ?? [];
        setPickVariantId(variants.length === 1 ? String(variants[0].id) : '');
    };

    const handleAddItem = () => {
        const variant = pickedVariants.find((v) => String(v.id) === pickVariantId);
        if (!pickedProduct || !variant || pickQuantity < 1) return;

        setItems((prev) => {
            // La misma presentación ya está: se suma la cantidad
            const existing = prev.find((item) => item.variant_id === variant.id);
            if (existing) {
                return prev.map((item) => (item === existing ? { ...item, quantity: item.quantity + pickQuantity } : item));
            }
            return [
                ...prev,
                {
                    id: genId('ci'),
                    product_id: pickedProduct.id,
                    variant_id: variant.id,
                    quantity: pickQuantity,
                    product_name: pickedProduct.name,
                    variant_name: variantLabel(pickedProduct, variant),
                    unit_price: Number(variant.price) || 0,
                    is_available: variant.products_variants_statuses_id === VARIANT_STATUS_ACTIVE,
                    image: variant.images?.[0] || pickedProduct.images?.[0] || variant.image || null,
                },
            ];
        });
        setPickProductId('');
        setPickVariantId('');
        setPickQuantity(1);
    };

    const updateItemQuantity = (id: string, quantity: number) =>
        setItems((prev) => prev.map((item) => (item.id === id ? { ...item, quantity: Math.max(1, quantity || 1) } : item)));

    const removeItem = (id: string) => setItems((prev) => prev.filter((item) => item.id !== id));

    // El orden es el que ve el cliente en "Incluye"
    const moveItem = (index: number, direction: -1 | 1) =>
        setItems((prev) => {
            const target = index + direction;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });

    const regularPrice = useMemo(() => items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0), [items]);
    const savings = regularPrice - (Number(formData.price) || 0);

    // ===================== GALERÍA =====================
    const handleFilesSelected = (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        const accepted = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
        setImages((prev) => [
            ...prev,
            ...accepted.map((file, i) => ({
                id: genId('img'),
                file,
                preview_url: URL.createObjectURL(file),
                position: prev.length + i,
                is_main: prev.length === 0 && i === 0,
            })),
        ]);
    };

    const handleSetMainImage = (id: string) => setImages((prev) => prev.map((img) => ({ ...img, is_main: img.id === id })));

    const handleRemoveImage = (id: string) =>
        setImages((prev) => {
            const target = prev.find((img) => img.id === id);
            if (target?.file) URL.revokeObjectURL(target.preview_url);
            const filtered = prev.filter((img) => img.id !== id);
            if (filtered.length > 0 && !filtered.some((img) => img.is_main)) {
                filtered[0] = { ...filtered[0], is_main: true };
            }
            return filtered;
        });

    // ===================== GUARDAR =====================
    const validate = (): string | null => {
        if (!formData.name.trim()) return 'El combo necesita un nombre.';
        if (formData.name.trim().length > NAME_MAX_LENGTH) return `El nombre admite máximo ${NAME_MAX_LENGTH} caracteres.`;
        if (!(Number(formData.price) >= 0)) return 'El precio no es válido.';
        if (items.length === 0) return 'Agrega al menos un producto al combo.';
        return null;
    };

    const buildFormData = (): FormData => {
        const fd = new FormData();
        const imgs = images.map((img, index) => ({ ...img, position: index }));
        const payload = {
            combo: { ...formData, name: formData.name.trim(), price: Number(formData.price) || 0, packaging_fee: Math.max(0, Number(formData.packaging_fee) || 0) },
            items: items.map((item) => ({ id: item.id, product_id: item.product_id, variant_id: item.variant_id, quantity: item.quantity })),
            // En edición: imágenes guardadas que se conservan (las demás se eliminan)
            images_existing: imgs.filter((img) => !img.file).map((img) => ({ id: img.id, position: img.position, is_main: img.is_main })),
        };
        fd.append('data', JSON.stringify(payload));
        imgs.forEach((img) => {
            if (img.file) {
                fd.append('images[]', img.file, img.file.name);
                fd.append('images_meta[]', JSON.stringify({ client_id: img.id, is_main: img.is_main, position: img.position }));
            }
        });
        return fd;
    };

    const handleSave = () => {
        const error = validate();
        if (error) {
            showToast({ title: 'Revisa el combo', message: error, type: 'warning', delay: 3500 });
            return;
        }
        setSaving(true);
        DomicilappService.saveCombo(storeId, buildFormData(), comboId)
            .then((response: any) => {
                if (response?.status === 200) {
                    showToast({ title: '¡Operación Exitosa!', message: isEdit ? 'Los cambios del combo se guardaron' : 'El combo se creó', type: 'success', delay: 3500 });
                    navigate('/combos/list');
                } else {
                    showToast({
                        title: '¡Ha ocurrido un error!',
                        message: typeof response?.data?.error === 'string' ? response.data.error : response?.message,
                        type: 'danger',
                        delay: 3500,
                    });
                }
            })
            .catch(() => showToast({ title: '¡Ha ocurrido un error!', message: 'Hubo un error guardando el combo', type: 'danger', delay: 3500 }))
            .finally(() => setSaving(false));
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <CSpinner size="sm" className="me-2" />
                <span className="text-body-secondary">Cargando combo...</span>
            </div>
        );
    }

    return (
        <div className="combo-form-page pb-5">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <CButton color="light" onClick={() => navigate('/combos/list')}>
                        <CIcon icon={cilArrowLeft} />
                    </CButton>
                    <div>
                        <h3 className="fw-bold text-body mb-0">{isEdit ? 'Editar combo' : 'Nuevo combo'}</h3>
                        <p className="text-body-secondary small mb-0">Productos fijos con un precio especial</p>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span className="text-body-secondary small">{formData.is_active ? 'Publicado' : 'Oculto'}</span>
                    <CFormSwitch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                </div>
            </div>

            <CRow className="g-4">
                <CCol lg={8}>
                    {/* ---- INFORMACIÓN ---- */}
                    <CCard className="border-0 shadow-sm rounded-3 mb-4">
                        <CCardBody className="p-4">
                            <h6 className="fw-bold text-body mb-3">Información</h6>
                            <div className="mb-3">
                                <CFormLabel className="small fw-semibold">Nombre</CFormLabel>
                                <CFormInput
                                    value={formData.name}
                                    maxLength={NAME_MAX_LENGTH}
                                    placeholder="Ej. Combo pareja"
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <CFormLabel className="small fw-semibold">Descripción</CFormLabel>
                                <CFormTextarea
                                    rows={3}
                                    value={formData.description}
                                    placeholder="Ej. Ideal para compartir: dos hamburguesas y una gaseosa grande."
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>
                        </CCardBody>
                    </CCard>

                    {/* ---- PRODUCTOS DEL COMBO ---- */}
                    <CCard className="border-0 shadow-sm rounded-3 mb-4">
                        <CCardBody className="p-4">
                            <h6 className="fw-bold text-body mb-1">Productos del combo</h6>
                            <p className="text-body-secondary small mb-3">Elige el producto, su presentación y cuántas unidades trae el combo.</p>

                            <div className="d-flex flex-wrap align-items-end gap-2 p-3 bg-body-tertiary rounded-3 border mb-3">
                                <div style={{ minWidth: '220px', flex: 2 }}>
                                    <CFormLabel className="small fw-semibold mb-1">Producto</CFormLabel>
                                    <CFormSelect size="sm" value={pickProductId} onChange={(e) => handlePickProduct(e.target.value)}>
                                        <option value="">Selecciona un producto</option>
                                        {products.map((p) => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </CFormSelect>
                                </div>
                                {pickedProduct && pickedVariants.length > 1 && (
                                    <div style={{ minWidth: '200px', flex: 2 }}>
                                        <CFormLabel className="small fw-semibold mb-1">Presentación</CFormLabel>
                                        <CFormSelect size="sm" value={pickVariantId} onChange={(e) => setPickVariantId(e.target.value)}>
                                            <option value="">Selecciona</option>
                                            {pickedVariants.map((v) => (
                                                <option key={v.id} value={v.id}>
                                                    {variantLabel(pickedProduct, v)} — {formatCOP(Number(v.price))}
                                                    {v.products_variants_statuses_id !== VARIANT_STATUS_ACTIVE ? ' (agotada)' : ''}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                    </div>
                                )}
                                <div style={{ width: '90px' }}>
                                    <CFormLabel className="small fw-semibold mb-1">Cantidad</CFormLabel>
                                    <CFormInput size="sm" type="number" min={1} value={pickQuantity} onChange={(e) => setPickQuantity(Math.max(1, Number(e.target.value) || 1))} />
                                </div>
                                <CButton color="dark" size="sm" disabled={!pickedProduct || !pickVariantId} onClick={handleAddItem}>
                                    <CIcon icon={cilPlus} size="sm" className="me-1" /> Agregar
                                </CButton>
                            </div>

                            {items.length === 0 ? (
                                <div className="p-4 text-center bg-body-tertiary rounded-3 border">
                                    <CIcon icon={cilLayers} size="xl" className="text-body-secondary mb-2" />
                                    <p className="mb-0 text-body-secondary small">El combo todavía no tiene productos.</p>
                                </div>
                            ) : (
                                <CTable responsive align="middle" className="mb-0">
                                    <CTableHead>
                                        <CTableRow>
                                            <CTableHeaderCell style={{ width: '56px' }}></CTableHeaderCell>
                                            <CTableHeaderCell className="small">Producto</CTableHeaderCell>
                                            <CTableHeaderCell className="small" style={{ width: '100px' }}>Cantidad</CTableHeaderCell>
                                            <CTableHeaderCell className="small text-end" style={{ width: '120px' }}>Por separado</CTableHeaderCell>
                                            <CTableHeaderCell style={{ width: '120px' }}></CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {items.map((item, index) => (
                                            <CTableRow key={item.id}>
                                                <CTableDataCell>
                                                    {item.image ? (
                                                        <img src={item.image} alt="" className="rounded-2" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <div className="rounded-2 bg-body-tertiary d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                                            <CIcon icon={cilImage} className="text-body-secondary" />
                                                        </div>
                                                    )}
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <div className="fw-semibold text-body">{item.product_name}</div>
                                                    {item.variant_name && <small className="text-body-secondary">{item.variant_name}</small>}
                                                    {!item.is_available && (
                                                        <CBadge color="warning-subtle" className="text-warning border border-warning-subtle ms-2 fw-normal">Agotado</CBadge>
                                                    )}
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormInput
                                                        size="sm"
                                                        type="number"
                                                        min={1}
                                                        value={item.quantity}
                                                        onChange={(e) => updateItemQuantity(item.id, Number(e.target.value))}
                                                    />
                                                </CTableDataCell>
                                                <CTableDataCell className="text-end text-body-secondary small">{formatCOP(item.unit_price * item.quantity)}</CTableDataCell>
                                                <CTableDataCell className="text-end text-nowrap">
                                                    <CButton color="light" size="sm" className="me-1" disabled={index === 0} onClick={() => moveItem(index, -1)}>
                                                        <CIcon icon={cilArrowTop} size="sm" />
                                                    </CButton>
                                                    <CButton color="light" size="sm" className="me-1" disabled={index === items.length - 1} onClick={() => moveItem(index, 1)}>
                                                        <CIcon icon={cilArrowBottom} size="sm" />
                                                    </CButton>
                                                    <CTooltip content="Quitar del combo">
                                                        <CButton color="light" size="sm" onClick={() => removeItem(item.id)}>
                                                            <CIcon icon={cilTrash} size="sm" />
                                                        </CButton>
                                                    </CTooltip>
                                                </CTableDataCell>
                                            </CTableRow>
                                        ))}
                                    </CTableBody>
                                </CTable>
                            )}
                        </CCardBody>
                    </CCard>

                    {/* ---- GALERÍA (combos_images) ---- */}
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <h6 className="fw-bold text-body mb-0">Imágenes del combo</h6>
                                <span className="text-body-secondary small">La imagen con ⭐ es la principal</span>
                            </div>
                            <p className="text-body-secondary small mb-3">Si no subes fotos, la app muestra las de los productos que incluye.</p>

                            <label
                                htmlFor="combo-images-input"
                                className="d-flex flex-column align-items-center justify-content-center gap-2 border rounded-3 bg-body-tertiary mb-3"
                                style={{ borderStyle: 'dashed', padding: '24px', cursor: 'pointer' }}
                            >
                                <CIcon icon={cilCloudUpload} size="xl" className="text-body-secondary" />
                                <span className="fw-semibold text-body small">Haz clic para subir imágenes</span>
                                <span className="text-body-secondary small">PNG, JPG (puedes seleccionar varias)</span>
                                <input
                                    id="combo-images-input"
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="d-none"
                                    onChange={(e) => {
                                        handleFilesSelected(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                            </label>

                            {images.length > 0 && (
                                <div className="d-flex flex-wrap gap-3">
                                    {images.map((img) => (
                                        <div key={img.id} className="border rounded-3 p-2 bg-body" style={{ width: '140px' }}>
                                            <div className="rounded-2 mb-2 bg-body-tertiary d-flex align-items-center justify-content-center overflow-hidden" style={{ width: '100%', height: '100px' }}>
                                                <img src={img.preview_url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <CTooltip content={img.is_main ? 'Imagen principal' : 'Marcar como principal'}>
                                                    <CButton color="link" className={`p-0 ${img.is_main ? 'text-warning' : 'text-secondary'}`} onClick={() => handleSetMainImage(img.id)}>
                                                        <CIcon icon={cilStar} />
                                                    </CButton>
                                                </CTooltip>
                                                <CButton color="link" className="text-secondary p-0" onClick={() => handleRemoveImage(img.id)}>
                                                    <CIcon icon={cilTrash} />
                                                </CButton>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CCardBody>
                    </CCard>
                </CCol>

                {/* ---- PRECIO Y RESUMEN ---- */}
                <CCol lg={4}>
                    <CCard className="border-0 shadow-sm rounded-3 position-sticky" style={{ top: '80px' }}>
                        <CCardBody className="p-4">
                            <h6 className="fw-bold text-body mb-3">Precio</h6>
                            <div className="mb-3">
                                <CFormLabel className="small fw-semibold">Precio del combo</CFormLabel>
                                <CFormInput
                                    type="number"
                                    min={0}
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                                />
                            </div>
                            <div className="mb-3">
                                <CFormLabel className="small fw-semibold">Costo de empaque (solo domicilio)</CFormLabel>
                                <CFormInput
                                    type="number"
                                    min={0}
                                    value={formData.packaging_fee}
                                    onChange={(e) => setFormData({ ...formData, packaging_fee: Math.max(0, Number(e.target.value) || 0) })}
                                />
                                <div className="text-body-secondary small mt-1">
                                    Se cobra por unidad solo cuando el pedido va a domicilio o programado. Déjalo en 0 si no cobras empaque.
                                </div>
                            </div>
                            <div className="mb-3">
                                <CFormLabel className="small fw-semibold">Stock (opcional)</CFormLabel>
                                <CFormInput
                                    type="number"
                                    min={0}
                                    placeholder="Sin límite"
                                    value={formData.stock}
                                    onChange={(e) => setFormData({ ...formData, stock: e.target.value === '' ? '' : Number(e.target.value) })}
                                />
                            </div>

                            <div className="border-top pt-3 small">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="text-body-secondary">Productos por separado</span>
                                    <span className="text-body">{formatCOP(regularPrice)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="text-body-secondary">Precio del combo</span>
                                    <span className="fw-bold text-body">{formatCOP(Number(formData.price) || 0)}</span>
                                </div>
                                {items.length > 0 && (
                                    savings > 0 ? (
                                        <div className="d-flex justify-content-between text-success fw-semibold">
                                            <span>El cliente ahorra</span>
                                            <span>{formatCOP(savings)} ({Math.round((savings / regularPrice) * 100)}%)</span>
                                        </div>
                                    ) : (
                                        <div className="text-warning">El combo no es más barato que comprar los productos por separado.</div>
                                    )
                                )}
                            </div>

                            <CButton color="dark" className="w-100 mt-4" disabled={saving} onClick={handleSave}>
                                {saving ? <CSpinner size="sm" className="me-1" /> : <CIcon icon={cilCheck} className="me-1" />}
                                {isEdit ? 'Guardar cambios' : 'Crear combo'}
                            </CButton>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
        </div>
    );
};

// Crear y editar comparten el formulario: se remonta al cambiar de combo para no arrastrar estado
const ComboFormPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    return <ComboForm key={id ?? 'new'} />;
};

export default ComboFormPage;
